// Storage usage of a PicField account. Only video storage has a limit (default 10 GB),
// image and total usage are information only (`limit: null`). All sizes in bytes.

export interface StorageUsageEntry {
  used: number
  count?: number
  limit?: number | null
}

/** Per-project usage — `ProfProject.storage_usage` and the entries of `ProfStorageResponse.projects` */
export interface ProjectStorageUsage {
  video: { used: number, count: number }
  image: { used: number, count: number }
  total: { used: number }
}

// GET /api/prof/storage/
export interface ProfStorageResponse {
  video: {
    used: number
    count: number
    limit: number
    remaining: number
    is_over_limit: boolean
  }
  image: { used: number, count: number, limit: null }
  total: { used: number, limit: null }
  /** Set by the daily check when the account was first found over its limit */
  video_storage_exceeded_at: string | null
  /** When the newest videos get deleted (exceeded_at + 7 days). null = not detected yet */
  video_storage_deletion_at: string | null
  /** All projects, largest video usage first */
  projects: Array<{ id: string, title: string, storage_usage: ProjectStorageUsage }>
}

export interface VideoToDelete {
  id: string
  file_name: string
  size: number
  created_at: string
  video_duration_seconds: number | null
  thumbnail: string | null
  project: { id: string, title: string } | null
  album: { id: string, title: string }
}

// GET /api/prof/storage/pending-deletion/ — the newest videos until enough is freed
export interface ProfStoragePendingDeletionResponse {
  /** Bytes over the limit */
  excess: number
  /** Bytes freed by deleting `files` */
  freed: number
  deletion_at: string | null
  files: VideoToDelete[]
}

/** German display of a byte size with binary units, e.g. `10,3 GB` */
export function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes || 0
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  if (unit === 0) return `${value} B`
  return `${value.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${units[unit]}`
}
